import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
const navItems = [{
  label: 'Home',
  path: '/'
}, {
  label: 'Properties',
  dropdown: [{
    label: 'Houses',
    path: '/houses'
  }, {
    label: 'Land',
    path: '/land'
  }]
}, {
  label: 'About Us',
  dropdown: [{
    label: 'Who We Are',
    path: '/who-we-are'
  }, {
    label: 'Certifications',
    path: '/certifications'
  }]
}, {
  label: 'Media',
  dropdown: [{
    label: 'Blog',
    path: '/blog'
  }, {
    label: 'Testimonials',
    path: '/testimonials'
  }]
}, {
  label: 'Contact',
  path: '/contact'
}];
export function Nav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const {
    scrollY
  } = useScroll();
  const location = useLocation();
  useMotionValueEvent(scrollY, 'change', latest => {
    setIsScrolled(latest > 50);
  });
  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);
  return <motion.nav initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled || location.pathname !== '/' ? 'bg-white/95 backdrop-blur-md py-3 shadow-sm border-b border-ink/5' : 'bg-bone/60 backdrop-blur-sm py-5'}`} data-id="element-590">
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-591">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-3 group z-50" data-id="element-592">
          <img src="https://cdn.magicpatterns.com/uploads/6F22E2Ctxndm5LAy6tPVXu/pasted-image.png" alt="Zylus Homes" className="h-10 w-auto" data-id="element-593" />
          <div className="hidden sm:flex flex-col leading-tight" data-id="element-594">
            <span className="font-serif text-lg tracking-tight text-ink font-medium" data-id="element-595">
              Zylus
            </span>
            <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-gold-dark -mt-0.5" data-id="element-596">
              Homes & Property
            </span>
          </div>
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8" data-id="element-597">
          {navItems.map(item => <div key={item.label} className="relative" onMouseEnter={() => setActiveDropdown(item.label)} onMouseLeave={() => setActiveDropdown(null)} data-id="element-598">
              {item.dropdown ? <button className="flex items-center gap-1 text-sm font-medium text-ink/80 hover:text-ink transition-colors py-2" data-id="element-599">
                  {item.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === item.label ? 'rotate-180' : ''}`} data-id="element-600" />
                </button> : <Link to={item.path!} className="text-sm font-medium relative group transition-colors duration-300 text-ink/80 hover:text-ink py-2 block" data-id="element-601">
                  {item.label}
                  <span className="absolute bottom-1 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full origin-left" data-id="element-602" />
                </Link>}

              {/* Dropdown Menu */}
              {item.dropdown && <AnimatePresence data-id="element-603">
                  {activeDropdown === item.label && <motion.div initial={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} transition={{
              duration: 0.2
            }} className="absolute top-full left-0 mt-1 w-56 bg-white rounded-2xl shadow-xl border border-ink/5 overflow-hidden py-2" data-id="element-604">
                      {item.dropdown.map(dropItem => <Link key={dropItem.label} to={dropItem.path} className="block px-5 py-2.5 text-sm text-ink/70 hover:text-ink hover:bg-ink/5 transition-colors" data-id="element-605">
                          {dropItem.label}
                        </Link>)}
                    </motion.div>}
                </AnimatePresence>}
            </div>)}
        </div>

        {/* Action */}
        <div className="hidden md:flex items-center" data-id="element-606">
          <Link to="/#book" className="bg-gold hover:bg-gold-dark text-ink font-semibold px-6 py-2.5 text-sm rounded-full transition-colors shadow-sm" data-id="element-607">
            Book Inspection
          </Link>
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-ink p-2 relative z-50" aria-label="Toggle menu" data-id="element-608">
          {mobileOpen ? <X className="w-5 h-5" data-id="element-609" /> : <Menu className="w-5 h-5" data-id="element-610" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence data-id="element-611">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: '100vh'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden fixed inset-0 top-0 bg-white pt-24 px-6 overflow-y-auto z-40" data-id="element-612">
            <div className="flex flex-col gap-2 pb-24" data-id="element-613">
              {navItems.map(item => <div key={item.label} className="border-b border-ink/5 last:border-0" data-id="element-614">
                  {item.dropdown ? <>
                      <button onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)} className="flex items-center justify-between w-full py-4 text-lg font-serif text-ink" data-id="element-615">
                        {item.label}
                        <ChevronDown className={`w-5 h-5 transition-transform ${mobileExpanded === item.label ? 'rotate-180' : ''}`} data-id="element-616" />
                      </button>
                      <AnimatePresence data-id="element-617">
                        {mobileExpanded === item.label && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-618">
                            <div className="flex flex-col gap-3 pb-4 pl-4 border-l-2 border-gold/30 ml-2" data-id="element-619">
                              {item.dropdown.map(dropItem => <Link key={dropItem.label} to={dropItem.path} className="text-ink/70 hover:text-gold text-base" data-id="element-620">
                                  {dropItem.label}
                                </Link>)}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </> : <Link to={item.path!} className="block py-4 text-lg font-serif text-ink hover:text-gold transition-colors" data-id="element-621">
                      {item.label}
                    </Link>}
                </div>)}
              <Link to="/#book" className="mt-6 bg-gold text-ink font-semibold px-6 py-4 text-base rounded-full text-center shadow-md" data-id="element-622">
                Book Inspection
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.nav>;
}